<script>
/**
 * Generic store-document print template. Handles the common shape used by
 * Purchase Orders, Sales Orders, Delivery Challans, GRNs, Quotations,
 * Material Issues, Gate Passes, etc. — basically anything that has a
 * company letterhead, meta block, party block(s), line-items table,
 * totals box, and signatories.
 *
 * Each host (`views/print/hosts/<Type>Host.vue`) fetches its own DTO and
 * maps it into these props. New document types add a host + a registration
 * in `InternalPrintHost.templateMap` — no new print component required for
 * the simple cases.
 *
 * Renders inside `<PrintLayout>`, so all the @page / watermark / multi-copy
 * / autoprint behaviour comes for free.
 */
export default {
  name: "StoreDocumentPrint",
  props: {
    /** Page title — e.g. "Purchase Order", "Delivery Challan". */
    title: { type: String, required: true },
    /** Optional subtitle band (e.g. "ORIGINAL FOR RECIPIENT"). */
    subtitle: { type: String, default: null },
    /** Voucher date — used by descendant T&C selector. */
    voucherDate: { type: [String, Date], default: null },
    /** Watermark text + state for cancelled/draft documents. */
    watermark: { type: String, default: null },
    watermarkState: { type: String, default: null },
    /** Voucher type identifier — controls which signatories / T&C / banks show. */
    voucherType: { type: String, default: null },
    /** Two-column meta items: `[{label, value}]`. */
    meta: { type: Array, default: () => [] },
    /** First party block — required (Bill To / Bill From / Vendor / Customer). */
    party: { type: Object, required: true },
    /** Optional second party (Ship To, Consignee). Pass `null` to hide. */
    secondaryParty: { type: Object, default: null },
    /** Table columns: `[{key, label, type?, width?, align?, formatter?}]`. */
    columns: { type: Array, required: true },
    /** Table rows. */
    rows: { type: Array, default: () => [] },
    /** Totals stack: `[{label, value, bold?, accent?, divider?}]`. */
    totals: { type: Array, default: () => [] },
    /** Show amount-in-words below totals. */
    grandTotal: { type: Number, default: 0 },
    showAmountInWords: { type: Boolean, default: true },
    /** Show the company-bank block — useful for invoices, not for POs/quotes. */
    showBank: { type: Boolean, default: false },
    /** Narration / notes line. */
    narration: { type: String, default: null },
    /** Whether to include T&C / signatories. Defaults true. */
    showTerms: { type: Boolean, default: true },
    showSignatories: { type: Boolean, default: true },
  },
};
</script>

<template>
  <PrintLayout
    :voucher-date="voucherDate"
    :watermark="watermark"
    :watermark-state="watermarkState"
    :title="title"
  >
    <PrintHeader />
    <PrintTitle :title="title" :subtitle="subtitle" :inherit-copy-label="!subtitle" />

    <PrintMetaGrid v-if="meta.length" :items="meta" :columns="2" />

    <div class="store-doc-party-row">
      <PrintPartyBlock v-bind="party" />
      <PrintPartyBlock v-if="secondaryParty" v-bind="secondaryParty" />
    </div>

    <PrintTable :columns="columns" :rows="rows" zebra>
      <template v-for="col in columns" :key="col.key" #[`cell-${col.key}`]="{ value, row, index }">
        <slot :name="`cell-${col.key}`" :value="value" :row="row" :index="index">
          <template v-if="col.formatter">{{ col.formatter(value, row) }}</template>
          <template v-else>{{ value }}</template>
        </slot>
      </template>
    </PrintTable>

    <PrintTotalsBox v-if="totals.length" :rows="totals" />

    <PrintAmountInWords v-if="showAmountInWords && grandTotal > 0" :amount="grandTotal" />

    <PrintBankDetailsBlock v-if="showBank" :voucher-type="voucherType" />

    <div v-if="narration" class="store-doc-narration">
      <span class="store-doc-narration-label">Narration:</span>
      <span class="store-doc-narration-value">{{ narration }}</span>
    </div>

    <PrintTermsBlock v-if="showTerms && voucherType" :voucher-type="voucherType" />

    <PrintSignatoryBlock v-if="showSignatories" :voucher-type="voucherType" />

    <PrintFooter />
  </PrintLayout>
</template>

<style scoped>
.store-doc-party-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4mm;
  margin: 0 0 3mm;
}

.store-doc-party-row > :only-child {
  grid-column: 1 / -1;
}

.store-doc-narration {
  font-size: var(--print-fs-xs);
  margin-top: 2mm;
  line-height: 1.4;
}

.store-doc-narration-label {
  color: var(--print-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 1mm;
  font-weight: 600;
}
</style>
